import React, { Component } from "react";
import FormInputs from "../../../components/UI/FormInputs";
import TableGrid from "../../../components/UI/TableGrid";
import { toQueryString, USDateTimeString } from "../../../utils/commonUtils";
import WithRoutify from "../../../hoc/WithRoutify";
import withReduxState from "../../../hoc/wReduxState";
import WithRouter from "../../../hoc/WithRouter";
import OrganizationService from "../../../services/OrganizationService";
import EmployeeCloneService from "../../../services/EmployeeCloneService";
import { error } from "../../../store/CommonSlice";

class CloneLogsList extends Component {
  constructor(props) {
    super(props);
    const tableHeaders = [
      {
        label: "From Org / Company",
        key: "fromLocation",
        sortable: false,
      },
      {
        label: "To Org / Company",
        key: "toLocation",
        sortable: false,
      },
      {
        label: "Patient",
        key: "sourcePatient",
        sortable: false,
      },
      {
        label: "Performed By",
        key: "performedBy",
      },
      {
        label: "Action",
        key: "action",
        sortable: false,
      },
      {
        label: "Date",
        key: "createdAt",
        sortName: "created_at",
        sortable: true,
      },
      {
        label: "Fields Copied",
        key: "fieldsCopied",
      },
      {
        label: "Reason",
        key: "reason",
      },
    ];
    this.state = {
      filters: {
        search_query: "",
      },
      tableRecords: {
        tableHeaders,
        tableRows: {
          status: 1000,
          data: [],
        },
        pagination: {
          page: 1,
          perPage: 25,
          totalRecords: 0,
          sort_by: "created_at",
          sort_order: "desc",
          search: {},
        },
      },
    };
  }

  componentDidMount() {
    this.fetchLogList();
  }

  onChangeFiltersHandler = (key, val) => {
    const { filters } = this.state;
    this.setState({ filters: { ...filters, [key]: val } });
  };

  submitFiltersHandler = () => {
    const { filters, tableRecords } = this.state;
    const queryFilters = {
      ...tableRecords.pagination,
      page: 1,
      search: filters,
    };
    this.fetchLogList(queryFilters);
  };

  clearFiltersHandler = () => {
    this.setState({ filters: { search_query: "" } }, () => {
      this.submitFiltersHandler();
    });
  };

  formatPatientCell = (employee) => {
    if (!employee) {
      return "—";
    }
    const name = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
    return name || "—";
  };

  /** Count field names stored on the audit row (not the 3 UI checkbox groups). */
  countClonedFields = (clonedFields) => {
    if (clonedFields == null) {
      return 0;
    }
    if (Array.isArray(clonedFields)) {
      return clonedFields.length;
    }
    if (typeof clonedFields === "string") {
      try {
        const parsed = JSON.parse(clonedFields);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch (e) {
        return 0;
      }
    }
    if (typeof clonedFields === "object") {
      return Object.keys(clonedFields).length;
    }
    return 0;
  };

  formatFieldsCopiedCell = (entry) => {
    const action = String(entry.action || "").toLowerCase();
    const count = this.countClonedFields(entry.cloned_fields);

    if (action === "archive") {
      return "—";
    }
    if (action === "reassign" && count === 0) {
      return "—";
    }
    if (count === 0) {
      return "0";
    }
    return String(count);
  };

  formatActionLabel = (action) => {
    const key = String(action || "").toLowerCase();
    if (key === "clone") return "Clone";
    if (key === "archive") return "Archive";
    if (key === "reassign") return "Transfer";
    return action || "—";
  };

  formatLocationCell = (organization, company, orgId, companyId) => {
    const orgName = organization?.name || orgId || "—";
    const companyName = company?.name || companyId || "—";
    return (
      <div>
        <div className="merge-refined-name">{orgName}</div>
        <div className="merge-refined-meta mt-1 small text-muted">{companyName}</div>
      </div>
    );
  };

  fetchLogList = async (query = {}) => {
    const { tableRecords } = this.state;
    const orgId =
      OrganizationService.getOrgId() || this.props.profile?.organization_id;
    if (!orgId) {
      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableRows: { status: 200, data: [] },
          pagination: {
            ...prev.tableRecords.pagination,
            ...query,
            totalRecords: 0,
          },
        },
      }));
      this.props.dispatch(
        error("Please select an organization to view clone history.")
      );
      return;
    }

    try {
      const queryParams = toQueryString({
        ...tableRecords.pagination,
        ...query,
      });
      const paramsWithOrg = { ...queryParams, orgId };
      this.props.Loader(true);
      const response = await EmployeeCloneService.getCloneLogs(paramsWithOrg);

      if (!response || !response.data) {
        this.setState((prev) => ({
          tableRecords: {
            ...prev.tableRecords,
            tableRows: { status: 200, data: [] },
            pagination: {
              ...prev.tableRecords.pagination,
              ...query,
              totalRecords: 0,
            },
          },
        }));
        return;
      }

      const tableData = (response.data || []).map((entry, idx) => {
        const source = entry.source_employee || entry.sourceEmployee;
        const sourceOrg = entry.source_organization || entry.sourceOrganization;
        const targetOrg = entry.target_organization || entry.targetOrganization;
        const sourceCompany = entry.source_company || entry.sourceCompany;
        const targetCompany = entry.target_company || entry.targetCompany;
        const actor = entry.actor;
        const actorName = actor
          ? `${actor.first_name || ""} ${actor.last_name || ""}`.trim()
          : "N/A";
        return {
          companyIndex: idx,
          isHeading: false,
          rowId: entry.audit_id,
          data: {
            fromLocation: {
              value: this.formatLocationCell(
                sourceOrg,
                sourceCompany,
                entry.source_org_id,
                entry.source_company_id
              ),
            },
            toLocation: {
              value: this.formatLocationCell(
                targetOrg,
                targetCompany,
                entry.target_org_id,
                entry.target_company_id
              ),
            },
            sourcePatient: {
              value: this.formatPatientCell(source),
            },
            performedBy: actorName,
            action: this.formatActionLabel(entry.action),
            createdAt: USDateTimeString(entry.created_at),
            fieldsCopied: this.formatFieldsCopiedCell(entry),
            reason: entry.reason || "—",
          },
        };
      });

      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableHeaders: prev.tableRecords.tableHeaders,
          tableRows: {
            type: "default",
            status: 200,
            data: tableData,
          },
          pagination: {
            ...prev.tableRecords.pagination,
            ...query,
            totalRecords: response.total ?? 0,
          },
        },
      }));
    } catch (err) {
      console.error("Error fetching clone logs", err);
      this.props.dispatch(error("Failed to load clone history"));
      this.setState((prev) => ({
        tableRecords: {
          ...prev.tableRecords,
          tableRows: { ...prev.tableRecords.tableRows, status: 200 },
        },
      }));
    } finally {
      this.props.Loader(false);
    }
  };

  render() {
    const { tableRecords, filters } = this.state;

    return (
      <div className="sh_cardBox sh_cardbglight">
        <div className="sh_cardHeader">
          <div className="form-group d-flex mb-0 justify-content-between align-items-center formheader">
            <h6 className="text-lg f-500 mb-0">Patient Transfer History Log</h6>
            <div className="d-flex">
              <div className="form-group-fields row mr-2">
                <div className="col-12 px-1 d-flex">
                  <div className="input-group input-group-joined input-group-solid">
                    <FormInputs
                      placeholder="Patient name, org, company, reason"
                      value={filters.search_query || ""}
                      changeHandler={(val) =>
                        this.onChangeFiltersHandler("search_query", val)
                      }
                      className="form-control"
                    />
                  </div>
                  <div className="btn-group pl-2">
                    <button
                      className="btn btn-primary"
                      onClick={() => this.submitFiltersHandler()}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => this.clearFiltersHandler()}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sh_cardBody p-0">
          <TableGrid
            hasPagination={true}
            fetchTableRecords={(filters) => this.fetchLogList(filters)}
            gridEditProps={{ activeEditId: null }}
            {...tableRecords}
          />
        </div>
      </div>
    );
  }
}

export default WithRouter(withReduxState(WithRoutify(CloneLogsList)));
